import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { Connection, FlowNode } from '@features/flow-ui-creator/interfaces/flow-ui.interface';
import { FlowService } from '@features/flow-ui-creator/services/flow.service';
import { DebugHelperService } from '@shared/services/debug-helper.service';
import { ReteModule } from 'rete-angular-plugin/21';
import { Position } from 'rete-render-utils/_types/types';
import { CenterPointPipe } from './pipes/center-point.pipe';

@Component({
  selector: 'connection',
  template: `
    @let connectionActionPosition = actionPosition();

    <svg
      data-testid="connection"
      [id]="'connection-' + data?.id"
      [attr.data-isloop]="data?.isLoop"
      (mouseenter)="onMouseenter($event)"
      (mousemove)="onMouseenter($event)"
      (mouseleave)="onMouseleave()"
      (click)="onConnectionClick($event)">
      <defs>
        <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stop-color="var(--start-color, #000000)" />
          <stop offset="50%" stop-color="var(--end-color, #000000)" />
        </linearGradient>
      </defs>
      <path #pathRef [attr.d]="path" />
    </svg>

    @if (showIds()) {
      <span
        class="debug-id"
        [style.left]="start | centerPoint: end : 'x'"
        [style.top]="start | centerPoint: end : 'y' : data.isLoop : pathRef"
        >{{ data.id }}</span
      >
    }

    @if (start && end && data.target) {
      <div
        class="connection-actions"
        [style.left]="connectionActionPosition.x"
        [style.top]="connectionActionPosition.y">
        <span class="action">
          <i
            class="mynaui-trash clickable"
            (pointerdown)="$event.stopPropagation()"
            (click)="removeConnection($event, data)"></i>
        </span>
      </div>
    }
  `,
  styleUrls: ['./custom-connection.component.scss'],
  imports: [ReteModule, CenterPointPipe],
})
export class CustomConnectionComponent implements OnInit, OnDestroy {
  @Input()
  data!: Connection<FlowNode>;

  @Input()
  start: Position;

  @Input()
  end: Position;

  @Input()
  path: string;

  pathRef = viewChild<ElementRef<SVGPathElement>>('pathRef');
  isHover = false;
  actionPosition = signal<{ x: string; y: string }>({
    x: '0px',
    y: '0px',
  });

  protected readonly showIds: Signal<boolean>;

  readonly #flowService = inject(FlowService);
  readonly #debugHelperService = inject(DebugHelperService);
  readonly #document = inject(DOCUMENT);
  readonly #renderer = inject(Renderer2);

  readonly #xOffset = 20;

  #documentClickListener: () => void;

  constructor() {
    this.showIds = this.#debugHelperService.shouldShowIds;
  }

  @HostBinding('class.hover')
  get hover(): boolean {
    return this.isHover;
  }

  ngOnInit(): void {
    this.#bindDocumentClickListener();
  }

  ngOnDestroy(): void {
    if (this.#documentClickListener) {
      this.#documentClickListener();
      this.#documentClickListener = null;
    }
  }

  onMouseenter(event: MouseEvent): void {
    this.isHover = true;
    const areaHolderPosition = this.#flowService.editorAreaHolderElement.getBoundingClientRect();
    const { transform } = this.#flowService.area?.area || { transform: { x: 0, y: 0, k: 1 } };

    this.actionPosition.set({
      x: `${(event.clientX - areaHolderPosition.left) / transform.k}px`,
      y: `${(event.clientY - areaHolderPosition.top) / transform.k + this.#xOffset}px`,
    });
  }

  onMouseleave(): void {
    this.isHover = false;
  }

  onConnectionClick(event: MouseEvent): void {
    this.#removeSelectedConnections();

    (event.target as SVGPathElement).closest('svg').classList.add('selected');
  }

  removeConnection(event: MouseEvent, connection: Connection<FlowNode>): void {
    event.stopPropagation();
    const entity = this.#flowService.translateService.instant('connection.entity-name');
    const header = this.#flowService.translateService.instant('confirm.entity.delete.header', {
      entity,
    });
    const message = this.#flowService.translateService.instant('confirm.entity.delete.confirmation', {
      entity: entity.toLowerCase(),
    });

    this.#flowService.openConfirmDialog(header, message, () => {
      this.#flowService.removeConnection(connection);
    });
  }

  #bindDocumentClickListener(): void {
    this.#documentClickListener = this.#renderer.listen(
      this.#document,
      'click',
      (event: MouseEvent) => {
        const selectedSvg = this.pathRef()?.nativeElement.closest('svg');

        if (!selectedSvg.contains(event.target as Node) && selectedSvg.classList.contains('selected')) {
          this.#removeSelectedConnections();
        }
      },
      {
        passive: true,
      },
    );
  }

  #removeSelectedConnections(): void {
    const connections = this.#document.querySelectorAll('connection svg');

    for (const connection of Array.from(connections)) {
      connection.classList.remove('selected');
    }
  }
}
